// ============================================
// Manor Miser - Shared Type Definitions
// ============================================

// -----------------------------
// Player Types
// -----------------------------

export type CharacterType = 'wizard' | 'farmer' | 'commoner' | 'warrior' | 'female';
export const CHARACTER_TYPES: CharacterType[] = ['wizard', 'farmer', 'commoner', 'warrior', 'female'];

export interface Player {
  id: string;
  name: string;
  isAlive: boolean;
  isConnected: boolean;
  isHost: boolean;
  isBot?: boolean;
  characterType: CharacterType;
}

// Extended player info - only sent to the player themselves
export interface PlayerPrivate extends Player {
  money: number;
  currentBet: number;
}

// Public player view - what others see
export interface PlayerPublic extends Player {
  // No money or bet info visible to others
}

// -----------------------------
// Game State Types
// -----------------------------

export type GamePhase = 'lobby' | 'betting' | 'reveal' | 'elimination' | 'gameOver';

export interface GameSettings {
  startingMoney: number;
  bettingDuration: number; // seconds
  minPlayers: number;
  maxPlayers: number;
}

// Game state sent to clients (sanitized)
export interface GameStatePublic {
  roomCode: string;
  phase: GamePhase;
  roundNumber: number;
  players: PlayerPublic[];
  hostId: string;
  settings: GameSettings;
  phaseEndsAt: number | null;
  eliminationOrder: string[];
}

// -----------------------------
// Bet Types
// -----------------------------

export type BetIncrement = 1 | 5 | 10;

export interface BetResult {
  playerId: string;
  playerName: string;
  amount: number;
}

// -----------------------------
// Statistics Types
// -----------------------------

export interface PlayerStats {
  playerId: string;
  playerName: string;
  placement: number;
  roundsSurvived: number;
  totalBet: number;
  biggestBet: number;
}

export interface GameStats {
  totalRounds: number;
  biggestPot: number;
  playerStats: PlayerStats[];
}

// -----------------------------
// Result Types
// -----------------------------

export interface EliminationResult {
  eliminatedPlayerId: string;
  eliminatedPlayerName: string;
  eliminatedBet: number;
  wasTieBreaker: boolean;
  tiedPlayerIds?: string[];
}

export interface RoundHistory {
  roundNumber: number;
  bets: { playerId: string; playerName: string; amount: number }[];
  eliminatedPlayerId: string | null;
}

export interface GameOverResult {
  winnerId: string;
  winnerName: string;
  winnerFinalMoney: number;
  finalStandings: PlayerStats[];
  stats: GameStats;
  roundHistory: RoundHistory[];
}
